import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect } from "react";
import { loginRequest } from "../config/msalConfig";
import SkypointLogoLoader from "./SkypointLogoLander/SkypointLogoLoader";

/**
 * ProtectedRoute Component
 *
 * Ensures that children components are only rendered when the user is authenticated.
 * Automatically redirects to login if no authenticated account is found.
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <YourProtectedComponent />
 * </ProtectedRoute>
 * ```
 *
 * @param children - React components to render when authenticated
 */
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    // Trigger login redirect if no authentication is in progress and no accounts exist
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      // Preserve the intended deep link across the B2C round-trip via TWO channels (mirrors
      // authService.login, the SMS-alert deep-link fix): sessionStorage as a fallback, AND the OAuth
      // `state` parameter as the PRIMARY channel — state travels through the auth provider and
      // survives sessionStorage being wiped (privacy mode, ITP, extensions, MSAL cache resets).
      // Without state, a cold click on a deep link while logged out (e.g. "View in skyCrm" from a
      // calendar event, or an alert email) could lose its destination and dump the user on the
      // default page. msalConfig.handleRedirectPromise rehydrates state -> sessionStorage; callback.tsx
      // then navigates there.
      const currentPath = window.location.pathname + window.location.search;
      const preserve = currentPath !== "/" && !currentPath.startsWith("/auth");
      if (preserve) {
        sessionStorage.setItem("redirectAfterLogin", currentPath);
      }
      instance.loginRedirect(preserve ? { ...loginRequest, state: currentPath } : loginRequest);
    }
  }, [inProgress, accounts, instance]);

  // Show loader while authentication interaction is in progress
  if (inProgress !== InteractionStatus.None) {
    return <SkypointLogoLoader />;
  }

  // Only render children if user is authenticated (has accounts)
  return accounts.length > 0 ? <>{children}</> : null;
};
